export interface TranscriptSegment {
  text: string;
  startTime: number;
  endTime: number;
  isDuringTts: boolean;
  processed: boolean;
}

export class VoiceTranscriptBuffer {
  private segments: TranscriptSegment[] = [];
  private maxDurationSec: number;

  constructor(maxDurationSec = 120) {
    this.maxDurationSec = maxDurationSec;
  }

  add(text: string, isDuringTts = false): TranscriptSegment {
    const now = Date.now() / 1000;
    const segment: TranscriptSegment = {
      text: text.trim(),
      startTime: now,
      endTime: now,
      isDuringTts,
      processed: false,
    };
    this.segments.push(segment);
    this.prune();
    return segment;
  }

  markTtsRange(startTime: number, durationSec: number): void {
    const endTime = startTime + durationSec;
    for (const s of this.segments) {
      if (s.startTime >= startTime && s.endTime <= endTime) {
        s.isDuringTts = true;
      }
    }
  }

  getAll(): TranscriptSegment[] {
    return [...this.segments];
  }

  getLastN(n: number): TranscriptSegment[] {
    return this.segments.slice(-n);
  }

  getLastSeconds(seconds: number): TranscriptSegment[] {
    const cutoff = Date.now() / 1000 - seconds;
    return this.segments.filter((s) => s.startTime >= cutoff);
  }

  getUnprocessed(): TranscriptSegment[] {
    return this.segments.filter((s) => !s.processed && !s.isDuringTts);
  }

  markProcessed(segment: TranscriptSegment): void {
    segment.processed = true;
  }

  formatForLLM(segments: TranscriptSegment[] = this.segments): string {
    if (segments.length === 0) return "No recent transcripts.";
    return segments
      .map((s) => {
        const marker = s.isDuringTts ? " [TTS]" : "";
        const ts = new Date(s.startTime * 1000).toLocaleTimeString();
        return `[${ts}]${marker} "${s.text}"`;
      })
      .join("\n");
  }

  private prune(): void {
    const cutoff = Date.now() / 1000 - this.maxDurationSec;
    this.segments = this.segments.filter((s) => s.endTime >= cutoff);
  }
}
